import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for audio recording with waveform visualization
 * @param {Function} onComplete - Callback when recording is complete, receives blob
 * @returns {Object} Recording state and control functions
 */
export default function useAudioRecording({ onComplete }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const durationRef = useRef(0);

  // Track duration when recording and not paused
  useEffect(() => {
    let interval;
    if (isRecording && !isPaused) {
      setDuration(0);
      durationRef.current = 0;
      interval = setInterval(() => {
        durationRef.current += 1;
        setDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording, isPaused]);

  // Draw waveform visualization
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;

    if (!canvas || !analyser) return;

    const canvasCtx = canvas.getContext('2d');
    if (!canvasCtx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!analyserRef.current) return;

      animationFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      // Clear canvas with gradient background
      const gradient = canvasCtx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#1f2937');
      gradient.addColorStop(1, '#111827');
      canvasCtx.fillStyle = gradient;
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw waveform
      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = '#3b82f6';
      canvasCtx.beginPath();

      const sliceWidth = canvas.width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const amplitude = (v - 1) * canvas.height * 0.9;
        const y = (canvas.height / 2) + amplitude;

        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();
    };

    draw();
  }, []);

  // Start recording (optionally accepts an existing stream to avoid microphone restart delay)
  const start = useCallback(async (existingStream = null) => {
    try {
      const stream = existingStream || await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Setup audio visualization
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      // Setup MediaRecorder
      let options = {};
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        options = { mimeType: 'audio/webm;codecs=opus' };
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        options = { mimeType: 'audio/webm' };
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        // Safari fallback
        options = { mimeType: 'audio/mp4' };
      }
      console.log('MediaRecorder options:', options);

      const recorder = new MediaRecorder(stream, options);
      console.log('MediaRecorder created:', {
        state: recorder.state,
        mimeType: recorder.mimeType
      });

      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.onstart = () => {
        console.log('✓ MediaRecorder STARTED, state:', recorder.state);
      };

      recorder.ondataavailable = (e) => {
        console.log(`📊 Data available: ${e.data.size} bytes, total chunks: ${chunksRef.current.length + 1}`);
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          console.log(`  ✓ Chunk added. Total chunks now: ${chunksRef.current.length}`);
        } else {
          console.warn('  ⚠ Empty data chunk received!');
        }
      };

      recorder.onerror = (e) => {
        console.error('❌ MediaRecorder ERROR:', e);
      };

      recorder.onstop = async () => {
        console.log('⏹ MediaRecorder STOPPED');
        console.log(`  Total chunks collected: ${chunksRef.current.length}`);

        // Use the actual MIME type that MediaRecorder used (fixes Safari MP4 support)
        // Extract base MIME type without codec details to avoid multipart form data corruption
        const baseMimeType = recorder.mimeType ? recorder.mimeType.split(';')[0] : 'audio/webm';
        const blob = new Blob(chunksRef.current, {
          type: baseMimeType
        });
        console.log(`📦 Recording blob created:`, {
          size: blob.size,
          type: blob.type,
          chunks: chunksRef.current.length
        });

        // Cleanup
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        analyserRef.current = null;

        setIsRecording(false);
        setIsPaused(false);
        setDuration(0);

        if (blob.size > 0 && onComplete) {
          await onComplete(blob, durationRef.current > 0 ? durationRef.current : null);
        } else {
          console.error('❌ Recording blob is EMPTY!');
          alert('Recording failed - no data captured.');
        }
      };

      // Start recording
      try {
        recorder.start(1000); // Collect data every second
        console.log(`🎬 Started audio recording with timeslice=1000ms`);
      } catch (startError) {
        console.error('Failed to start with timeslice, trying without:', startError);
        recorder.start();
        console.log(`🎬 Started audio recording without timeslice`);
      }

      setIsRecording(true);
      setIsPaused(false);

      // Start visualization
      drawWaveform();
    } catch (error) {
      console.error('❌ Error starting audio recording:', error);
      alert('Failed to access microphone: ' + error.message);
    }
  }, [onComplete, drawWaveform]);

  // Stop recording
  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  // Pause recording
  const pause = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.pause();
      setIsPaused(true);
    }
  }, []);

  // Resume recording
  const resume = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'paused') {
      recorderRef.current.resume();
      setIsPaused(false);
    }
  }, []);

  return {
    isRecording,
    isPaused,
    duration,
    canvasRef,
    start,
    stop,
    pause,
    resume
  };
}
